/**
 * Session types matching backend API contracts
 */

export enum SessionStatus {
  WAITING = 'WAITING',
  LIVE = 'LIVE',
  COMPLETE = 'COMPLETE',
}

export type SessionClosureReason = 'TEACHER_ENDED' | 'AUTO_CLOSED_TIMEOUT' | 'SYSTEM_ERROR';

export interface Session {
  sessionId: string;
  code: string;
  teacherId: string;
  classId?: string; // Optional class folder for organization
  status: SessionStatus;
  sessionName?: string;
  requiredPlayers: number;
  allowLateJoins: boolean;
  characterPool: string[];
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  closureReason?: SessionClosureReason;
}

export interface SessionPlayer {
  sessionId: string;
  playerId: string;
  displayName: string;
  fullName?: string;
  joinedAt: string;
  role?: string;
  energy: number;
  email?: string;
}

export interface SessionWithPlayers extends Session {
  players: SessionPlayer[];
  joinedCount: number;
}

export interface CreateSessionRequest {
  classId: string; // Required: Class folder for this session
  sessionName?: string;
  code?: string;
  requiredPlayers?: number;
  allowLateJoins?: boolean;
  characterPool?: string[];
}

export interface CreateSessionResponse {
  session: Session;
}

export interface StartSessionResponse {
  sessionId: string;
  status: SessionStatus;
  startedAt: string;
  playerCount: number;
  rolesAssigned: Array<{
    playerId: string;
    displayName: string;
    role: string;
  }>;
}

export interface CompleteSessionResponse {
  sessionId: string;
  status: SessionStatus;
  completedAt: string;
  expiresAt: number;
  message: string;
}

export interface DashboardMetrics {
  totalSessions: number;
  activeSessions: number;
  completedSessions: number;
  waitingSessions: number;
  liveSessions: number;
}

export interface ListSessionsResponse {
  sessions: SessionWithPlayers[];
  metrics: DashboardMetrics;
}

export interface JoinSessionRequest {
  code: string;
  displayName: string;
  fullName?: string;
}

export interface JoinSessionResponse {
  sessionId: string;
  playerId: string;
  displayName: string;
  fullName?: string;
  joinedAt: string;
  sessionStatus: string;
  currentPlayers: number;
  requiredPlayers: number;
}

export interface SessionStatusResponse {
  sessionId: string;
  code: string;
  sessionName: string | null;
  status: SessionStatus;
  requiredPlayers: number;
  allowLateJoins: boolean;
  joinedCount: number;
  closureReason?: SessionClosureReason | null;
  players: Array<{
    playerId: string;
    displayName: string;
    joinedAt: string;
    role: string | null;
    energy?: number;
  }>;
  // Game state (only present when session is LIVE)
  gameState?: {
    currentStage: number;
    currentStep: string;
    currentVariant: 'A' | 'B' | null;
    huntingPermitDay: number;
    teamName: string;
    teamEnergyPacks: number;
    inventory: Array<{
      id: string;
      name: string;
      quantity: number;
    }>;
    flags: {
      stage5Variant: 'A' | 'B';
      fuelLeakDiscovered: boolean;
      diverPoisoned: boolean;
      containerLost: boolean;
      rainbowRayCaptured: boolean;
      cleanupPartial: boolean;
      cleanupFull: boolean;
      goldenOctopusCaptured: boolean;
      missionComplete: boolean;
      allDead: boolean;
    };
    gameStatus: 'waiting' | 'in_progress' | 'completed' | 'failed';
    players: Array<{
      playerId: string;
      name: string;
      role: string;
      energy: number;
      isAlive: boolean;
      isPoisoned: boolean;
      submissionStatus?: Record<string, boolean>;
    }>;
    npcs: Array<{
      role: 'DOC' | 'GEE';
      energy: number;
      decisions: Record<string, any>;
    }>;
    stateVersion: number;
  };
  // Current player's detailed state (only present when session is LIVE)
  currentPlayerState?: {
    playerId: string;
    role: string;
    name: string;
    energy: number;
    isAlive: boolean;
    isPoisoned: boolean;
    swimming: boolean;
    scores: {
      brain: number;
      heart: number;
      task: number;
      communication: number;
    };
    decisions: Record<string, any>;
  };
}

export interface PlayerResult {
  sessionId: string;
  playerId: string;
  displayName: string;
  fullName?: string;
  email?: string;
  role: string;
  energy: number;
  score: number;
  rank: number;
}

export interface SessionResultsResponse {
  session: {
    sessionId: string;
    code: string;
    sessionName?: string;
    status: SessionStatus;
    createdAt: string;
    startedAt?: string;
    completedAt?: string;
  };
  results: PlayerResult[];
}

export interface MoveSessionRequest {
  classId: string | null; // Target classId or null for uncategorized
}

export interface MoveSessionResponse {
  session: Session;
}

export interface GameStateResponse {
  sessionId: string;
  status: SessionStatus;
  latestStateVersion?: number;
  state?: {
    currentStage: number;
    currentStep: string;
    currentVariant: 'A' | 'B' | null;
    huntingPermitDay: number;
    teamName: string;
    teamEnergyPacks: number;
    inventory: Array<{
      id: string;
      name: string;
      quantity?: number;
    }>;
    flags: {
      stage5Variant: 'A' | 'B';
      fuelLeakDiscovered: boolean;
      diverPoisoned: boolean;
      containerLost: boolean;
      rainbowRayCaptured: boolean;
      cleanupPartial: boolean;
      cleanupFull: boolean;
      goldenOctopusCaptured: boolean;
      missionComplete: boolean;
      allDead: boolean;
    };
    gameStatus: 'waiting' | 'in_progress' | 'completed' | 'failed';
    players: Array<{
      playerId: string;
      name: string;
      role: string;
      energy: number;
      isAlive: boolean;
      isPoisoned: boolean;
      swimming?: boolean;
      scores?: {
        brain: number;
        heart: number;
        task: number;
        communication: number;
      };
      decisions?: Record<string, any>;
    }>;
    npcs?: Array<{
      role: 'DOC' | 'GEE';
      energy: number;
      decisions?: Record<string, any>;
    }>;
    decisions?: Array<{
      questionId: string;
      playerDecisions: Array<{
        playerId: string;
        questionId: string;
        answer: any;
        timestamp: string;
      }>;
      finalAnswer?: any;
      decisionMethod: string;
      timestamp: string;
    }>;
  };
}
